// Aignoz Innovation OS — Phase 6.1 pricing page (outcome-first).
// Pure client-side. No backend, no billing, no usage tracking.
import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap, Rocket, Users, Building2, Microscope, Stethoscope,
  Boxes, TrendingUp, Landmark, Check, CircleDot, Lock, ArrowRight,
  Calculator, Mail, Sparkles, BadgeCheck, BadgeDollarSign, Wallet,
  Globe, Layers, ShieldCheck, Cpu, Clock, Star, Zap, AlertCircle,
  FlaskConical, ScrollText, Network, Compass, ArrowDown,
  Linkedin, Calendar, Handshake, Bug, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { usePersona, PersonaIcon } from "@/persona";
import { apiGet } from "@/lib";

// ============================================================================
// Footer configuration — update these placeholders before launch
// ============================================================================
const FOOTER_CONFIG = {
  founderTitle: "Dr. Sriinidhi BV, MBBS, MD · Founder & CEO, Aignoz Healthcare Solutions",
  founderSub: "Healthcare Innovation Strategist · AI × Biotechnology × DeepTech",
  linkedinUrl: "https://www.linkedin.com/in/dr-sriinidhi-bv-a953637/",
  websiteUrl: "https://www.aignoz.com/",
  email: "aignozai@gmail.com",
  location: "Bengaluru, India",
  copyrightYear: 2026,
  legalEntity: "Aignoz Health Solutions",
};

// ============================================================================
// Formatters
// ============================================================================
const formatINR = (n) => {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(0)}K`;
  return `₹${n.toLocaleString("en-IN")}`;
};
const formatINRMonth = (n) => n.toLocaleString("en-IN");
const round05 = (n) => Math.round(n * 2) / 2;

// ============================================================================
// Plan + program data
// ============================================================================
const FEATURE_TAGS = {
  today: { icon: Check, color: "text-emerald-300" },
  beta: { icon: CircleDot, color: "text-amber-300" },
  soon: { icon: Lock, color: "text-white/40" },
};

function FeatureLine({ kind, children }) {
  const cfg = FEATURE_TAGS[kind];
  const Icon = cfg.icon;
  return (
    <li className="flex items-start gap-2 text-[11px] leading-relaxed">
      <Icon className={`w-3 h-3 mt-0.5 shrink-0 ${cfg.color}`} />
      <span className={kind === "soon" ? "text-white/45" : "text-white/80"}>{children}</span>
    </li>
  );
}

function FeatureGroup({ title, items, kind }) {
  if (!items.length) return null;
  return (
    <div className="space-y-1.5">
      <div className="text-[9px] uppercase tracking-[0.16em] text-white/35">{title}</div>
      <ul className="space-y-1">
        {items.map((it, i) => <FeatureLine key={i} kind={kind}>{it}</FeatureLine>)}
      </ul>
    </div>
  );
}

function PlanCard({ plan, annual, popular }) {
  const navigate = useNavigate();
  const { setPersona } = usePersona();
  const Icon = plan.icon;
  const renderPrice = () => {
    if (plan.priceMonthly == null) {
      return (
        <div>
          <div className="text-3xl font-semibold tracking-tight">{plan.priceLabel}</div>
          {plan.priceSub && <div className="text-[11px] text-white/45 mt-1">{plan.priceSub}</div>}
        </div>
      );
    }
    if (annual) {
      const annualINR = Math.round(plan.priceMonthly * 12 * 0.8);
      const monthlyEq = Math.round(annualINR / 12);
      return (
        <div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-3xl font-semibold tracking-tight">₹{formatINRMonth(annualINR)}</span>
            <span className="text-[11px] text-white/45">/year</span>
          </div>
          <div className="text-[11px] text-white/60 mt-1">
            ≈ ₹{formatINRMonth(monthlyEq)}/month · (~${Math.round(monthlyEq / 84)}/mo)
          </div>
          <div className="text-[10px] text-emerald-300 mt-0.5">Save 20%</div>
        </div>
      );
    }
    return (
      <div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-3xl font-semibold tracking-tight">₹{formatINRMonth(plan.priceMonthly)}</span>
          <span className="text-[11px] text-white/45">/month</span>
        </div>
        <div className="text-[11px] text-white/45 mt-1">(~${Math.round(plan.priceMonthly / 84)}/month)</div>
      </div>
    );
  };
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.15 }}
      className={`relative rounded-2xl border p-5 flex flex-col gap-4 bg-white/[0.02] ${
        popular ? "border-teal-400/40 shadow-[0_0_0_1px_rgba(94,234,212,0.18),0_18px_60px_-20px_rgba(94,234,212,0.25)] lg:-mt-3 lg:mb-3" : "border-white/[0.06]"
      }`}
      data-testid={`plan-${plan.id}`}
    >
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-teal-400 text-slate-950 text-[9px] uppercase tracking-[0.18em] font-bold flex items-center gap-1">
          <Star className="w-2.5 h-2.5" /> Most Popular
        </div>
      )}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-teal-400/10 border border-teal-400/30">
            <Icon className="w-4 h-4 text-teal-300" />
          </div>
          <div className="text-[9px] uppercase tracking-[0.18em] text-white/40">{plan.id.toUpperCase()}</div>
        </div>
        <div className="text-2xl font-semibold tracking-tight">{plan.name}</div>
        <div className="text-[11px] text-white/55 mt-1 leading-relaxed">{plan.tagline}</div>
      </div>
      <div className="border-t border-white/[0.06] pt-4 min-h-[88px]">{renderPrice()}</div>
      <div className="border-t border-white/[0.06] pt-4 space-y-4 flex-1">
        <FeatureGroup title="Available today" items={plan.today} kind="today" />
        <FeatureGroup title="Beta" items={plan.beta} kind="beta" />
        <FeatureGroup title="Coming soon" items={plan.soon} kind="soon" />
      </div>
      <Button
        data-testid={`plan-cta-${plan.id}`}
        onClick={() => {
          if (plan.cta.type === "free") {
            setPersona("student"); toast.success("Welcome, Student. Free forever."); navigate("/");
          } else if (plan.cta.type === "trial" || plan.cta.type === "sales") {
            toast.success("Trial signups launching soon. Routing to enterprise team.");
            navigate(`/pricing/enterprise-inquiry?type=${plan.id}`);
          } else if (plan.cta.type === "contact") {
            navigate("/pricing/enterprise-inquiry");
          }
        }}
        className={popular
          ? "bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold w-full"
          : "w-full border border-teal-400/30 text-teal-100 hover:bg-teal-400/10 bg-transparent"
        }
      >
        {plan.cta.label} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
      </Button>
    </motion.div>
  );
}

const PRIMARY_PLANS = [
  {
    id: "student", name: "Student", icon: GraduationCap,
    priceMonthly: null, priceLabel: "Free", priceSub: "Free forever",
    tagline: "Learn how healthcare innovation gets built.",
    today: [
      "Innovation Engine (Discovery)",
      "Innovation Matrix (1,500 cells, read-only)",
      "Opportunity Heatmap",
      "Featured Ventures (FirstMile Sepsis, ZoneSight Diagnostics)",
      "Pitch Deck viewer",
      "Educational tooltips & methodology guides",
    ],
    beta: [],
    soon: ["Venture generation (3/month)", "Founder Copilot", "PDF / JSON export"],
    cta: { label: "Start Free", type: "free" },
  },
  {
    id: "founder", name: "Founder", icon: Rocket, priceMonthly: 4999,
    tagline: "Build the next healthcare venture.",
    today: [
      "Everything in Student", "Venture Studio (full generation)",
      "Founder Copilot™", "TRL tracking & next milestones", "Founder Review AI critique",
      "Final Venture Report (PDF + JSON export)",
      "Persona switching (Founder / Investor / Researcher)",
      "50 venture generations/month",
    ],
    beta: ["Venture Evolution Mode", "Executive Memos"],
    soon: ["Grant Matching", "Investor Matching", "Incubator Matching"],
    cta: { label: "Start 14-day trial", type: "trial" },
    popular: true,
  },
  {
    id: "startup", name: "Startup", icon: Users, priceMonthly: 14999,
    tagline: "Run the venture engine for your whole team.",
    today: ["Everything in Founder", "250 venture generations/month", "Multiple personas per workspace"],
    beta: ["Team workspace", "Venture Portfolio view"],
    soon: ["Venture Progression Engine", "Prototype Lab", "Investor Readiness scoring", "Multi-user collaboration", "Cohort analytics"],
    cta: { label: "Talk to Sales", type: "sales" },
  },
  {
    id: "enterprise", name: "Enterprise", icon: Building2,
    priceMonthly: null, priceLabel: "Custom", priceSub: "Annual contract",
    tagline: "Pharma, MedTech, and global healthcare innovators.",
    today: ["Unlimited venture generations", "All Founder + Startup features", "Priority support"],
    beta: ["API access", "Organization workspaces"],
    soon: ["White-label deployment", "Private models / on-prem", "Custom agents", "SLA tiers", "Dedicated success team"],
    cta: { label: "Contact Enterprise Team", type: "contact" },
  },
];

const FUTURE_PROGRAMS = [
  { id: "researcher", name: "Researcher", icon: Microscope, price: "₹999/mo", usd: "~$12/mo", audience: "Academic & translational scientists", status: "Coming Q3 2026" },
  { id: "clinician", name: "Clinician", icon: Stethoscope, price: "₹4,999/mo", usd: "~$59/mo", audience: "Hospital innovators & clinician-entrepreneurs", status: "Coming Q3 2026" },
  { id: "incubator", name: "Incubator", icon: Boxes, price: "₹49,999/mo", usd: "~$599/mo", audience: "BioNEST, BIRAC, accelerators", status: "Coming Q4 2026" },
  { id: "investor", name: "Investor", icon: TrendingUp, price: "₹99,999/mo", usd: "~$1,199/mo", audience: "VCs, family offices, angel networks", status: "Coming Q4 2026" },
  { id: "government", name: "Government", icon: Landmark, price: "Custom", usd: "", audience: "Ministries, ICMR, NITI Aayog", status: "Pilot stage" },
];

const PERSONA_ACTIONS = [
  { id: "founder", icon: Rocket, name: "Founder", action: "Build ventures faster" },
  { id: "researcher", icon: Microscope, name: "Researcher", action: "Discover opportunities" },
  { id: "clinician", icon: Stethoscope, name: "Clinician", action: "Evaluate clinical feasibility" },
  { id: "investor", icon: TrendingUp, name: "Investor", action: "Evaluate venture quality" },
  { id: "incubator", icon: Boxes, name: "Incubator", action: "Source startups" },
  { id: "enterprise", icon: Building2, name: "Enterprise", action: "Scout innovation" },
  { id: "government", icon: Landmark, name: "Government", action: "Map innovation ecosystems" },
];

const REVENUE_MODEL = [
  { id: 1, title: "Subscriptions", sub: "Per-seat pricing across Founder, Startup, Researcher, Clinician plans", icon: Wallet },
  { id: 2, title: "Incubator Licenses", sub: "Annual cohort licenses for BioNEST and accelerators", icon: Boxes },
  { id: 3, title: "Enterprise Licenses", sub: "Custom annual contracts for pharma, MedTech, diagnostics", icon: Building2 },
  { id: 4, title: "Investor Intelligence", sub: "Premium deal flow & diligence subscriptions", icon: TrendingUp, soon: true },
  { id: 5, title: "Future Marketplace", sub: "Success-based services on grants, pilots, and partnerships", icon: Sparkles, soon: true },
];

const COMPARISON_ROWS = [
  { stage: "Problem Discovery", trad: "3-6 months", aignoz: "Minutes" },
  { stage: "Market Research", trad: "₹2-10 Lakhs", aignoz: "Included" },
  { stage: "Technology Evaluation", trad: "2-3 months", aignoz: "Minutes" },
  { stage: "Prototype Planning", trad: "₹5-25 Lakhs", aignoz: "Included" },
  { stage: "Regulatory Consulting", trad: "₹2-20 Lakhs", aignoz: "Included" },
  { stage: "Grant Discovery", trad: "Weeks of search", aignoz: "Coming Soon", soon: true },
  { stage: "Incubator Discovery", trad: "Weeks of outreach", aignoz: "Coming Soon", soon: true },
  { stage: "Investor Preparation", trad: "2-3 months", aignoz: "Founder Copilot™" },
];

const CONSULTANT_COSTS = [
  { kind: "Market Research Consultant", range: "₹2–10 Lakhs", icon: Compass },
  { kind: "Innovation Strategy Consultant", range: "₹5–20 Lakhs", icon: Sparkles },
  { kind: "Regulatory Consultant", range: "₹5–25 Lakhs", icon: ScrollText },
  { kind: "Clinical Strategy Consultant", range: "₹3–15 Lakhs", icon: Stethoscope },
  { kind: "Grant Consultant", range: "₹1–5 Lakhs", icon: BadgeDollarSign },
];

// ============================================================================
// Pre-launch metrics — pulls from /api/runs
// ============================================================================
function usePlatformMetrics() {
  const [state, setState] = useState({ loading: true, error: false, runs: [] });
  useEffect(() => {
    let cancelled = false;
    apiGet("/runs", { useCache: false })
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r) ? r : (r?.runs || []);
        setState({ loading: false, error: false, runs: list });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, error: true, runs: [] }); });
    return () => { cancelled = true; };
  }, []);

  return useMemo(() => {
    const { loading, error, runs } = state;
    const complete = runs.filter((r) => r.status === "complete");
    // Summary endpoint doesn't include nested outputs — use downstream-artifact heuristic.
    // `scores` is computed after both founder_review and validation_planner agents run.
    const withFounder = runs.filter((r) => r.founder_review || r.outputs?.founder_review || (r.scores && Object.keys(r.scores).length > 0)).length;
    const withValidation = runs.filter((r) => r.outputs?.validation_planner || (r.scores && Object.keys(r.scores).length > 0)).length;
    // Compute compute-time: sum of (completed_at - created_at) for complete runs, plus any trace durations available.
    let computeMs = 0;
    for (const r of runs) {
      if (r.completed_at && r.created_at) {
        const d = new Date(r.completed_at).getTime() - new Date(r.created_at).getTime();
        if (d > 0 && d < 1000 * 60 * 60 * 6) computeMs += d; // skip anomalies > 6h
      }
      if (Array.isArray(r.trace)) {
        for (const t of r.trace) {
          if (typeof t.duration_ms === "number") computeMs += t.duration_ms;
          else if (typeof t.duration_s === "number") computeMs += t.duration_s * 1000;
        }
      }
    }
    return {
      loading, error,
      ventures: runs.length,
      endToEnd: complete.length,
      techsEval: 14 * complete.length,
      founderReviews: withFounder,
      validationRoadmaps: withValidation,
      computeMs,
      hoursSavedEst: complete.length * 720,
    };
  }, [state]);
}

const fmtCompute = (ms) => {
  if (!ms) return "0m";
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m}m`;
};

// ============================================================================
// CountUp
// ============================================================================
function CountUp({ value, prefix = "", suffix = "", decimals = 0 }) {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const start = display;
    const end = value;
    const dur = 350;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const v = start + (end - start) * (1 - Math.pow(1 - p, 3));
      setDisplay(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const formatted = decimals > 0
    ? display.toFixed(decimals)
    : Math.round(display).toLocaleString("en-IN");
  return <span className="mono">{prefix}{formatted}{suffix}</span>;
}

// ============================================================================
// Inline ROI Calculator
// ============================================================================
const TYPE_MULT = { student: 0.3, researcher: 0.5, founder: 1.0, startup: 1.2, enterprise: 1.5 };
const STAGE_MULT = { concept: 0.4, prototype: 0.7, validation: 1.0, pilot: 1.2, commercial: 1.4 };
const STAGES = [
  { id: "concept", label: "Concept" },
  { id: "prototype", label: "Prototype" },
  { id: "validation", label: "Validation" },
  { id: "pilot", label: "Pilot" },
  { id: "commercial", label: "Commercial" },
];
const COSTS = {
  marketResearch: 6,
  innovationStrategy: 12,
  regulatoryConsult: 15,
  clinicalStrategy: 9,
  fundraisingPrep: 5,
};

function ROIInline() {
  const [founderType, setFounderType] = useState("founder");
  const [team, setTeam] = useState(5);
  const [stage, setStage] = useState("prototype");
  const [budgetLakh, setBudgetLakh] = useState([50]);

  const m = useMemo(() => {
    const sm = STAGE_MULT[stage];
    const tm = TYPE_MULT[founderType];
    const traditionalMonths = Math.round(12 * sm);
    const aignozMonths = Math.max(1, Math.round(traditionalMonths * 0.4));
    const timeSavedMonths = traditionalMonths - aignozMonths;
    const consultingCostAvoided = round05(COSTS.marketResearch * sm * tm + COSTS.innovationStrategy * sm * tm);
    const validationPlanningSavings = round05(COSTS.clinicalStrategy * sm * tm);
    const regulatoryPlanningSavings = round05(COSTS.regulatoryConsult * sm * tm);
    const fundraisingPrepSavings = round05(COSTS.fundraisingPrep * sm * tm);
    const totalValueLakh = consultingCostAvoided + validationPlanningSavings + regulatoryPlanningSavings + fundraisingPrepSavings;
    return {
      timeSavedMonths, traditionalMonths, aignozMonths,
      consultingCostAvoided, validationPlanningSavings,
      regulatoryPlanningSavings, fundraisingPrepSavings,
      totalValueLakh,
    };
  }, [founderType, team, stage, budgetLakh]);

  const budgetLabel = budgetLakh[0] >= 100 ? `₹${(budgetLakh[0] / 100).toFixed(1)} Cr` : `₹${budgetLakh[0]} L`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Inputs */}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-6" data-testid="roi-inputs">
        <div>
          <label className="text-xs text-white/70 font-semibold block mb-2">Founder Type</label>
          <Select value={founderType} onValueChange={setFounderType}>
            <SelectTrigger data-testid="roi-foundertype"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="student">Student</SelectItem>
              <SelectItem value="researcher">Researcher</SelectItem>
              <SelectItem value="founder">Founder</SelectItem>
              <SelectItem value="startup">Startup</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-white/70 font-semibold block mb-2">Team Size</label>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setTeam((n) => Math.max(1, n - 1))}
              data-testid="roi-team-minus" className="h-9 w-9 p-0 border-white/[0.08]">−</Button>
            <Input type="number" min={1} max={50} value={team}
              onChange={(e) => setTeam(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
              data-testid="roi-team-input"
              className="text-center text-base font-semibold mono w-20" />
            <Button variant="outline" size="sm" onClick={() => setTeam((n) => Math.min(50, n + 1))}
              data-testid="roi-team-plus" className="h-9 w-9 p-0 border-white/[0.08]">+</Button>
            <span className="text-xs text-white/45 ml-2">people</span>
          </div>
        </div>
        <div>
          <label className="text-xs text-white/70 font-semibold block mb-2">Venture Stage</label>
          <div className="grid grid-cols-5 gap-1.5" data-testid="roi-stage-group">
            {STAGES.map((s) => (
              <button key={s.id}
                data-testid={`roi-stage-${s.id}`}
                onClick={() => setStage(s.id)}
                className={`py-2 rounded-md border text-[11px] font-semibold transition ${
                  stage === s.id
                    ? "border-teal-400/50 bg-teal-400/[0.1] text-teal-100"
                    : "border-white/[0.08] text-white/55 hover:bg-white/[0.03]"
                }`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-white/70 font-semibold">Estimated Budget</label>
            <span className="text-sm mono text-teal-200">{budgetLabel}</span>
          </div>
          <Slider value={budgetLakh} onValueChange={setBudgetLakh} min={10} max={1000} step={5}
            data-testid="roi-budget-slider" />
          <div className="flex justify-between text-[10px] text-white/35 mt-1.5">
            <span>₹10 L</span><span>₹10 Cr</span>
          </div>
        </div>
      </div>

      {/* Outputs */}
      <div className="space-y-3" data-testid="roi-outputs">
        <div className="grid grid-cols-2 gap-3">
          <OutTile testId="roi-time-saved" label="Time Saved" accent="emerald"
            value={<><CountUp value={m.timeSavedMonths} /> <span className="text-sm text-white/55 font-normal">months</span></>}
            sub="earlier to market" />
          <OutTile testId="roi-consulting" label="Consulting Cost Avoided" accent="teal"
            value={<>₹<CountUp value={m.consultingCostAvoided} decimals={m.consultingCostAvoided % 1 ? 1 : 0} /> <span className="text-sm text-white/55 font-normal">L</span></>}
            sub="market + strategy" />
          <OutTile testId="roi-validation" label="Validation Planning Savings" accent="teal"
            value={<>₹<CountUp value={m.validationPlanningSavings} decimals={m.validationPlanningSavings % 1 ? 1 : 0} /> <span className="text-sm text-white/55 font-normal">L</span></>}
            sub="clinical strategy" />
          <OutTile testId="roi-regulatory" label="Regulatory Planning Savings" accent="teal"
            value={<>₹<CountUp value={m.regulatoryPlanningSavings} decimals={m.regulatoryPlanningSavings % 1 ? 1 : 0} /> <span className="text-sm text-white/55 font-normal">L</span></>}
            sub="reg pathway" />
          <OutTile testId="roi-fundraising" label="Fundraising Prep Savings" accent="teal" className="col-span-2"
            value={<>₹<CountUp value={m.fundraisingPrepSavings} decimals={m.fundraisingPrepSavings % 1 ? 1 : 0} /> <span className="text-sm text-white/55 font-normal">L</span></>}
            sub="investor materials" />
        </div>

        <div className="rounded-lg border border-teal-400/25 bg-teal-400/[0.05] p-3 flex items-center justify-between" data-testid="roi-total">
          <span className="text-xs text-white/70">Total estimated value</span>
          <span className="text-lg font-semibold mono text-teal-200">
            ₹<CountUp value={m.totalValueLakh} decimals={m.totalValueLakh % 1 ? 1 : 0} /> Lakhs
          </span>
        </div>

        <div className="text-[10px] text-white/45 leading-relaxed">
          Estimates based on median consulting rates and traditional healthcare venture-development timelines.{" "}
          <span className="text-white/65 font-semibold">Estimates, not guarantees.</span>
        </div>

        <a href="#plans" className="block">
          <Button data-testid="roi-cta-founder"
            className="w-full bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold">
            Start with Founder Plan <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </a>
      </div>
    </div>
  );
}

function OutTile({ label, value, sub, accent, testId, className = "" }) {
  const colors = { emerald: "text-emerald-300", teal: "text-teal-200", amber: "text-amber-300" };
  return (
    <div className={`rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 ${className}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{label}</div>
      <div className={`text-2xl font-semibold tracking-tight mt-1.5 ${colors[accent] || "text-white"}`}>{value}</div>
      {sub && <div className="text-[10px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

// ============================================================================
// Pricing page
// ============================================================================
export function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const navigate = useNavigate();
  const metrics = usePlatformMetrics();

  return (
    <div className="px-8 py-8 max-w-[1400px] mx-auto" data-testid="pricing-page">
      {/* SECTION 1 — Header */}
      <div className="flex items-end justify-between gap-6 mb-12 flex-wrap" id="top">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-teal-300/80">Pricing & Plans</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            Built for every stakeholder in healthcare innovation.
          </h1>
          <p className="text-sm text-white/55 italic mt-2 max-w-2xl">
            Generate ventures, guide founders, scout opportunities, and run portfolios — at every stage.
          </p>
        </div>
      </div>

      {/* SECTION 2 — Why Aignoz Exists */}
      <section id="why" className="mb-14" data-testid="why-aignoz">
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 border-l-2 border-l-teal-400/60">
          <h2 className="text-2xl font-semibold tracking-tight mb-4">Why Aignoz Exists</h2>
          <div className="space-y-3 max-w-4xl">
            <p className="text-sm text-white/75 leading-relaxed">
              Healthcare innovation is fragmented across researchers, clinicians, founders, regulators, incubators, and investors.
              Each step takes months. Each stakeholder uses different tools. Each handoff loses context.
            </p>
            <p className="text-sm text-white/75 leading-relaxed">
              Aignoz Innovation OS unifies these workflows into a single operating system that guides ventures
              from concept to commercialization — discovering the right opportunities, engineering the right ventures,
              and adapting to every stakeholder.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — Why Aignoz? comparison */}
      <section id="comparison" className="mb-14" data-testid="comparison">
        <div className="mb-4">
          <h2 className="text-2xl font-semibold tracking-tight">Why Aignoz?</h2>
          <p className="text-sm text-white/55 mt-1">Traditional healthcare venture-building vs Aignoz.</p>
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-3 border-b border-white/[0.06] text-[10px] uppercase tracking-[0.14em] text-white/40">
            <div className="col-span-4">Stage</div>
            <div className="col-span-4">Traditional</div>
            <div className="col-span-4 text-teal-300">Aignoz Innovation OS</div>
          </div>
          {COMPARISON_ROWS.map((row, i) => (
            <div key={i} className="grid grid-cols-12 items-center px-5 py-3 border-b border-white/[0.03] last:border-0">
              <div className="col-span-4 text-sm font-medium text-white/85">{row.stage}</div>
              <div className="col-span-4 text-sm text-white/55">{row.trad}</div>
              <div className={`col-span-4 text-sm flex items-center gap-1.5 ${row.soon ? "text-white/45" : "text-teal-200"}`}>
                {row.soon
                  ? <><Lock className="w-3 h-3 text-white/35" /> {row.aignoz}</>
                  : <><Check className="w-3.5 h-3.5 text-emerald-300" /> {row.aignoz}</>}
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm text-white/55 italic mt-4 max-w-3xl">
          A 6-month, ₹15-75 Lakh consulting engagement, replaced by an operating system that runs in minutes.
        </p>
      </section>

      {/* SECTION 4 — Who Should Use Aignoz (action verbs) */}
      <section id="who" className="mb-14" data-testid="who-uses">
        <div className="mb-4">
          <h2 className="text-2xl font-semibold tracking-tight">Who Should Use Aignoz?</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {PERSONA_ACTIONS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.id}
                data-testid={`persona-tile-${p.id}`}
                className="rounded-lg border border-white/[0.06] bg-white/[0.015] p-3 text-center hover:bg-white/[0.025] transition">
                <div className="w-7 h-7 rounded-md mx-auto mb-2 flex items-center justify-center bg-teal-400/10 border border-teal-400/25">
                  <Icon className="w-3.5 h-3.5 text-teal-300" />
                </div>
                <div className="text-xs font-semibold mb-0.5">{p.name}</div>
                <div className="text-[10px] text-white/50 leading-snug">{p.action}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SECTION 5 — What Would This Cost Without Aignoz */}
      <section id="cost" className="mb-14" data-testid="cost-without">
        <div className="mb-4">
          <h2 className="text-2xl font-semibold tracking-tight">What Would This Cost Without Aignoz?</h2>
          <p className="text-sm text-white/55 mt-1">Estimated cost of engaging traditional healthcare innovation consultants for a single venture.</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-2">
            {CONSULTANT_COSTS.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} data-testid={`cost-row-${i}`}
                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center bg-white/[0.04] border border-white/[0.05]">
                    <Icon className="w-3.5 h-3.5 text-white/55" />
                  </div>
                  <div className="text-sm text-white/80 flex-1">{c.kind}</div>
                  <div className="text-sm mono text-white/65">{c.range}</div>
                </div>
              );
            })}
            <div data-testid="cost-total"
              className="rounded-lg border border-amber-400/30 bg-amber-400/[0.05] p-4 flex items-center justify-between mt-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-amber-200/80">Total traditional cost</div>
                <div className="text-[10px] text-white/45 mt-0.5">per venture, traditional approach</div>
              </div>
              <div className="text-2xl font-semibold mono text-amber-200">₹15 Lakhs – ₹75+ Lakhs</div>
            </div>
          </div>

          <div className="lg:col-span-2 rounded-2xl border border-teal-400/30 bg-teal-400/[0.04] p-5 flex flex-col justify-between"
            data-testid="cost-with-aignoz">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80 mb-2">With Aignoz</div>
              <div className="space-y-3">
                <div>
                  <div className="text-sm font-semibold">Founder Plan</div>
                  <div className="text-base mono text-teal-200">₹4,999/month <span className="text-xs text-white/55">(~$59/mo)</span></div>
                  <div className="text-[11px] text-white/55 mt-0.5">Full venture studio access</div>
                </div>
                <div className="border-t border-white/[0.06] pt-3">
                  <div className="text-sm font-semibold">Startup Plan</div>
                  <div className="text-base mono text-teal-200">₹14,999/month <span className="text-xs text-white/55">(~$179/mo)</span></div>
                  <div className="text-[11px] text-white/55 mt-0.5">Team workspace + 250 ventures</div>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/[0.06]">
              <div className="text-sm font-semibold italic text-teal-100">Estimated savings of 95%+ vs traditional consulting.</div>
              <div className="text-[10px] text-white/40 mt-1">Based on median consulting rates. Estimate, not a guarantee.</div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — ROI Calculator INLINE */}
      <section id="roi" className="mb-14" data-testid="roi-calculator-section">
        <div className="mb-4">
          <h2 className="text-2xl font-semibold tracking-tight">Estimate Your Savings</h2>
          <p className="text-sm text-white/55 mt-1">Calculate the value Aignoz creates for your stage and team.</p>
        </div>
        <ROIInline />
      </section>

      {/* SECTION 7 — Pre-Launch Platform Metrics */}
      <section id="metrics" className="mb-14" data-testid="prelaunch-metrics">
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <h2 className="text-2xl font-semibold tracking-tight">Aignoz Innovation OS — Platform Metrics</h2>
          <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-200" data-testid="prelaunch-tag">
            Pre-Launch
          </span>
        </div>
        <p className="text-sm text-white/55 mt-1 mb-5">
          Based on platform-generated ventures, validation runs, and internal testing.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <MetricCard testId="m-ventures" label="Ventures Generated"
            value={metrics.loading ? "—" : metrics.error ? "—" : metrics.ventures} />
          <MetricCard testId="m-endtoend" label="End-to-End Venture Runs"
            value={metrics.loading ? "—" : metrics.error ? "—" : metrics.endToEnd} />
          <MetricCard testId="m-techs" label="Technologies Evaluated"
            value={metrics.loading ? "—" : metrics.error ? "—" : metrics.techsEval} sub="14 categories × completed runs" />
          <MetricCard testId="m-founder" label="Founder Reviews Generated"
            value={metrics.loading ? "—" : metrics.error ? "—" : metrics.founderReviews} />
          <MetricCard testId="m-validation" label="Validation Roadmaps Generated"
            value={metrics.loading ? "—" : metrics.error ? "—" : metrics.validationRoadmaps} />
          <MetricCard testId="m-compute" label="Total Agent Compute Time"
            value={metrics.loading ? "—" : metrics.error ? "—" : fmtCompute(metrics.computeMs)} sub="cumulative across runs" />
        </div>
        {(metrics.loading || metrics.error) && (
          <div className="text-[11px] text-white/40 mt-3" data-testid="metrics-status">
            {metrics.loading ? "Metrics syncing…" : "Metrics syncing… (offline)"}
          </div>
        )}

        {/* Derived stat */}
        <div className="mt-5 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 flex items-center justify-between" data-testid="m-hours-saved">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-white/40">Estimated Hours Saved</div>
            <div className="text-3xl font-semibold mono text-teal-200 mt-1">
              {metrics.loading ? "—" : metrics.hoursSavedEst.toLocaleString("en-IN")}
              <span className="text-sm text-white/55 font-normal ml-1.5">hrs</span>
            </div>
            <div className="text-[10px] text-white/45 mt-1.5 italic">
              Estimated based on traditional healthcare venture-development timelines.
            </div>
          </div>
          <div className="hidden md:flex flex-col items-end text-right">
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/35">Calc</div>
            <div className="text-[11px] text-white/55 mono mt-1">{metrics.endToEnd || 0} runs × 720 hrs</div>
          </div>
        </div>

        <div className="text-[11px] text-white/45 leading-relaxed mt-4 border-t border-white/[0.04] pt-4" data-testid="prelaunch-footer">
          Metrics reflect platform-generated test runs and internal evaluations.{" "}
          <span className="text-white/65 font-semibold">We are not yet in commercial production.</span>
        </div>
      </section>

      {/* SECTION 8 — Plans */}
      <section id="plans" className="mb-14">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Plans</h2>
            <p className="text-sm text-white/55 mt-1">Pick your stage. Aignoz adapts to your role.</p>
          </div>
          <div className="flex items-center gap-3 rounded-full border border-white/[0.08] bg-white/[0.02] px-4 py-2" data-testid="billing-toggle">
            <span className={`text-xs ${!annual ? "text-white" : "text-white/45"}`}>Monthly</span>
            <Switch checked={annual} onCheckedChange={setAnnual} data-testid="billing-toggle-switch" />
            <span className={`text-xs ${annual ? "text-white" : "text-white/45"}`}>Annual</span>
            {annual && <span className="text-[10px] uppercase tracking-wider text-emerald-300 ml-1">save 20%</span>}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PRIMARY_PLANS.map((p) => (
            <PlanCard key={p.id} plan={p} annual={annual} popular={p.popular} />
          ))}
        </div>
      </section>

      {/* SECTION 9 — Future Programs */}
      <section id="future" className="mb-14" data-testid="future-programs">
        <div className="mb-4">
          <h2 className="text-2xl font-semibold tracking-tight">Future Programs</h2>
          <p className="text-xs text-white/50 mt-1">Specialized plans launching as we expand into adjacent personas.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {FUTURE_PROGRAMS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.id} data-testid={`future-${p.id}`}
                className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-teal-300/70" />
                  <div className="text-sm font-semibold">{p.name}</div>
                </div>
                <div>
                  <div className="text-base font-semibold mono">{p.price}</div>
                  {p.usd && <div className="text-[10px] text-white/40">{p.usd}</div>}
                </div>
                <div className="text-[10px] text-white/55 leading-relaxed min-h-[28px]">{p.audience}</div>
                <div className="text-[9px] uppercase tracking-wider text-amber-300/80">{p.status}</div>
                <button
                  data-testid={`future-notify-${p.id}`}
                  onClick={() => toast.success("Added to waitlist — we'll be in touch.")}
                  className="mt-1 text-[11px] px-2 py-1 rounded-md border border-white/[0.08] text-white/70 hover:border-teal-400/30 hover:text-teal-100 transition self-start">
                  Notify me
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* SECTION 10 — Venture Progression Vision */}
      <section id="vision" className="mb-14" data-testid="vision">
        <div className="mb-4">
          <h2 className="text-2xl font-semibold tracking-tight">The Venture Progression Vision</h2>
          <p className="text-sm text-white/55 mt-1">How Aignoz evolves from a venture generator into a full healthcare innovation operating system.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <VisionCol kind="today" title="Today" testId="vision-today"
            items={["Healthcare Venture Generation", "Founder Review AI", "Venture Scorecard"]} />
          <VisionCol kind="next" title="Next" testId="vision-next"
            items={["Founder Copilot™", "TRL Intelligence™", "Venture Progression Engine™"]} />
          <VisionCol kind="future" title="Future" testId="vision-future"
            items={["Grant Matching", "Incubator Matching", "Lab Matching", "Pilot Site Matching", "Investor Matching", "Healthcare Innovation Marketplace"]} />
        </div>
      </section>

      {/* SECTION 11 — Revenue Model */}
      <section id="revenue" className="mb-14" data-testid="revenue-model">
        <div className="mb-4">
          <h2 className="text-2xl font-semibold tracking-tight">Revenue Model</h2>
          <p className="text-xs text-white/50 mt-1">How Aignoz scales from product to platform.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {REVENUE_MODEL.map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.id} data-testid={`revenue-${r.id}`}
                className={`rounded-xl border p-4 flex flex-col gap-2 ${r.soon ? "border-white/[0.04] bg-white/[0.01]" : "border-white/[0.08] bg-white/[0.02]"}`}>
                <div className="flex items-center justify-between">
                  <Icon className={`w-4 h-4 ${r.soon ? "text-white/35" : "text-teal-300/80"}`} />
                  {r.soon && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/[0.06] text-white/45 border border-white/[0.08]">Coming Soon</span>}
                </div>
                <div className={`text-sm font-semibold tracking-tight ${r.soon ? "text-white/65" : ""}`}>{r.title}</div>
                <div className="text-[10px] text-white/45 leading-relaxed">{r.sub}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SECTION 12 — Footer CTA strip (Enterprise only — ROI is inline now) */}
      <section className="mb-10" data-testid="footer-cta">
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 flex items-center justify-between gap-4">
          <div>
            <div className="text-base font-semibold">Run a healthcare innovation org?</div>
            <div className="text-xs text-white/55 mt-1">Tailored deployments for pharma, MedTech, and providers.</div>
          </div>
          <Button
            data-testid="cta-enterprise"
            onClick={() => navigate("/pricing/enterprise-inquiry")}
            variant="outline"
            className="border-teal-400/30 text-teal-100 hover:bg-teal-400/10 shrink-0">
            <Mail className="w-3.5 h-3.5 mr-1.5" /> Talk to Enterprise <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </section>

      {/* Founder + legal footer */}
      <FounderLegalFooter />
    </div>
  );
}

// ============================================================================
// Founder + legal site footer
// ============================================================================
function FounderLink({ href, to, icon: Icon, label, testId }) {
  const base = "inline-flex items-center gap-2 text-sm text-white/55 hover:text-white/90 transition-colors duration-200";
  const inner = (
    <>
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </>
  );
  if (to) {
    return <Link to={to} className={base} data-testid={testId}>{inner}</Link>;
  }
  return (
    <a href={href} target={href?.startsWith("http") ? "_blank" : undefined}
       rel={href?.startsWith("http") ? "noopener" : undefined}
       className={base} data-testid={testId}>{inner}</a>
  );
}

function FounderLegalFooter() {
  return (
    <section
      data-testid="founder-footer"
      className="border-t border-white/[0.06] bg-white/[0.015] -mx-8 px-8 pt-24"
    >
      {/* Zone 1 — Founder identity */}
      <div className="max-w-[640px] mx-auto text-center" data-testid="founder-identity">
        <img src="/aignoz-logo.png" alt="Aignoz" className="h-12 w-12 mx-auto mb-6 opacity-90 object-contain" />

        <h3 className="text-xl font-semibold text-white/95 mb-1">
          {FOOTER_CONFIG.founderTitle}
        </h3>

        <p className="text-sm text-white/60 mb-6">
          {FOOTER_CONFIG.founderSub}
        </p>

        <p className="text-base text-white/80 leading-relaxed mb-6">
          Building the future of healthcare venture creation through Aignoz Innovation OS.
        </p>

        <p className="text-sm text-white/65 leading-relaxed mb-6">
          Mission: Empower researchers, clinicians, founders, incubators, and investors to transform
          healthcare challenges into validated, venture-ready innovations.
        </p>

        <p className="text-sm text-white/50 mb-8 inline-flex items-center justify-center gap-2">
          <MapPin className="h-3.5 w-3.5" />
          {FOOTER_CONFIG.location}
        </p>

        <p className="text-lg font-medium text-white/90 italic mb-1">
          "Building the operating system for healthcare innovation."
        </p>

        <p className="text-base font-semibold text-[#5EEAD4] mt-6 mb-2">
          Aignoz Innovation OS™
        </p>
        <p className="text-sm text-white/55 tracking-wide">
          Discover · Design · Validate · Fund · Commercialize Healthcare Innovations
        </p>
      </div>

      {/* Zone 2 — Links row */}
      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 mt-16 mb-12"
        data-testid="founder-links">
        <FounderLink href={FOOTER_CONFIG.linkedinUrl} icon={Linkedin} label="LinkedIn" testId="footer-linkedin" />
        <FounderLink href={FOOTER_CONFIG.websiteUrl} icon={Globe} label="Website" testId="footer-website" />
        <FounderLink href={`mailto:${FOOTER_CONFIG.email}`} icon={Mail} label="Email" testId="footer-email" />
        <FounderLink to="/pricing/enterprise-inquiry?type=demo" icon={Calendar} label="Book a Demo" testId="footer-demo" />
        <FounderLink to="/pricing/enterprise-inquiry?type=partner" icon={Handshake} label="Partner with Aignoz" testId="footer-partner" />
        <FounderLink href={`mailto:${FOOTER_CONFIG.email}?subject=Issue%20Report`} icon={Bug} label="Report an Issue" testId="footer-report" />
      </div>

      {/* Zone 3 — Legal */}
      <div className="border-t border-white/[0.04] mt-12 pt-8 pb-12" data-testid="footer-legal">
        <div className="max-w-[800px] mx-auto text-center space-y-3">
          <p className="text-xs text-white/45">
            © {FOOTER_CONFIG.copyrightYear} {FOOTER_CONFIG.legalEntity}. All rights reserved.
          </p>
          <p className="text-[11px] text-white/35 leading-relaxed max-w-[640px] mx-auto">
            Aignoz Innovation OS™ is an AI-powered healthcare innovation platform intended for research,
            venture development, and innovation planning. It does not provide medical advice, diagnosis,
            or treatment recommendations.
          </p>
        </div>
      </div>
    </section>
  );
}

function MetricCard({ label, value, sub, testId }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{label}</div>
      <div className="text-3xl font-semibold mono text-teal-200 mt-1.5">{value}</div>
      {sub && <div className="text-[10px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

function VisionCol({ title, items, kind, testId }) {
  const cfg = {
    today: { accent: "border-emerald-400/40 bg-emerald-400/[0.04]", color: "text-emerald-300", Icon: Check, label: "Shipped" },
    next: { accent: "border-amber-400/40 bg-amber-400/[0.04]", color: "text-amber-300", Icon: ArrowRight, label: "In development" },
    future: { accent: "border-white/[0.06] bg-white/[0.015]", color: "text-white/45", Icon: Lock, label: "Roadmap" },
  }[kind];
  const Icon = cfg.Icon;
  return (
    <div className={`rounded-2xl border p-5 ${cfg.accent}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-lg font-semibold tracking-tight">{title}</div>
        <div className={`text-[10px] uppercase tracking-[0.14em] ${cfg.color}`}>{cfg.label}</div>
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Icon className={`w-3.5 h-3.5 mt-0.5 ${cfg.color} shrink-0`} />
            <span className={kind === "future" ? "text-white/65" : "text-white/85"}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================================
// /pricing/roi-calculator — redirect to /pricing#roi
// ============================================================================
export function ROICalculator() {
  return <Navigate to="/pricing#roi" replace />;
}

// ============================================================================
// /pricing/enterprise-inquiry (unchanged)
// ============================================================================

const ROLES = ["VP Strategy", "R&D Lead", "Innovation Director", "CEO", "Investment Lead", "Other"];
const ORG_TYPES = ["Pharma", "Diagnostics", "MedTech", "Healthcare Provider", "Government Agency", "Investment Firm", "Incubator", "Other"];
const TEAM_BUCKETS = ["1-10", "11-50", "51-200", "200+"];

const ENT_SIDEBAR = [
  { icon: Cpu, text: "Custom agent configurations" },
  { icon: Layers, text: "White-label deployment" },
  { icon: Globe, text: "API access" },
  { icon: ShieldCheck, text: "Private model hosting" },
  { icon: BadgeCheck, text: "SLA & dedicated success team" },
  { icon: Clock, text: "Implementation in 4-6 weeks" },
];

export function EnterpriseInquiry() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ org: "", name: "", email: "", role: "", orgType: "", teamSize: "", useCase: "" });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.org || form.org.trim().length < 3) e.org = "Organization name required (3+ chars)";
    if (!form.name) e.name = "Your name required";
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid work email required";
    if (!form.role) e.role = "Pick a role";
    if (!form.orgType) e.orgType = "Pick an organization type";
    if (!form.teamSize) e.teamSize = "Pick a team size";
    if (!form.useCase || form.useCase.trim().length < 20) e.useCase = "20+ characters please";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    try {
      const key = "aignoz.enterprise_inquiries";
      const list = JSON.parse(localStorage.getItem(key) || "[]");
      list.push({ ...form, submitted_at: new Date().toISOString() });
      localStorage.setItem(key, JSON.stringify(list));
    } catch { /* ignore */ }
    setSubmitted(true);
  };

  const reset = () => {
    setForm({ org: "", name: "", email: "", role: "", orgType: "", teamSize: "", useCase: "" });
    setErrors({});
    setSubmitted(false);
  };

  return (
    <div className="px-8 py-8 max-w-[1300px] mx-auto" data-testid="enterprise-inquiry">
      <div className="mb-7">
        <div className="text-[10px] uppercase tracking-[0.2em] text-teal-300/80">Enterprise Inquiry</div>
        <h1 className="text-3xl font-semibold tracking-tight mt-1">Let's talk about your healthcare innovation strategy.</h1>
        <p className="text-sm text-white/55 mt-1.5 max-w-3xl">
          Tailored deployments for pharma, MedTech, diagnostics, providers, and public health organizations.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div key="ok" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                className="py-12 text-center" data-testid="inquiry-success">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ delay: 0.15, type: "spring", stiffness: 220 }}
                  className="w-16 h-16 rounded-full bg-teal-400/15 border border-teal-400/40 flex items-center justify-center mx-auto mb-5">
                  <Check className="w-8 h-8 text-teal-300" />
                </motion.div>
                <div className="text-xl font-semibold tracking-tight">Got it. Our team will reach out within 24 hours.</div>
                <p className="text-sm text-white/55 mt-2">We've logged your inquiry locally for this demo. In production, this would route to the Aignoz enterprise team.</p>
                <button data-testid="inquiry-reset" onClick={reset}
                  className="text-sm text-teal-300 hover:text-teal-200 mt-5 inline-flex items-center gap-1.5">
                  Submit another <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            ) : (
              <motion.form key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="inquiry-form">
                <Field label="Organization name" error={errors.org}>
                  <Input data-testid="inq-org" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} placeholder="e.g. Lupin Diagnostics" />
                </Field>
                <Field label="Your name" error={errors.name}>
                  <Input data-testid="inq-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Priya Sharma" />
                </Field>
                <Field label="Work email" error={errors.email}>
                  <Input data-testid="inq-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="priya@lupin.com" />
                </Field>
                <Field label="Role" error={errors.role}>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="inq-role"><SelectValue placeholder="Select your role" /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Organization type" error={errors.orgType}>
                  <Select value={form.orgType} onValueChange={(v) => setForm({ ...form, orgType: v })}>
                    <SelectTrigger data-testid="inq-orgtype"><SelectValue placeholder="Select org type" /></SelectTrigger>
                    <SelectContent>
                      {ORG_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Team size" error={errors.teamSize}>
                  <div className="flex gap-2" data-testid="inq-teamsize-group">
                    {TEAM_BUCKETS.map((t) => (
                      <button key={t} type="button" data-testid={`inq-team-${t}`}
                        onClick={() => setForm({ ...form, teamSize: t })}
                        className={`flex-1 py-1.5 rounded-md border text-xs transition ${
                          form.teamSize === t
                            ? "border-teal-400/40 bg-teal-400/[0.08] text-teal-100"
                            : "border-white/[0.08] text-white/55 hover:bg-white/[0.03]"
                        }`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Use case" error={errors.useCase} className="md:col-span-2">
                  <Textarea data-testid="inq-usecase" value={form.useCase}
                    onChange={(e) => setForm({ ...form, useCase: e.target.value })}
                    rows={5} maxLength={1000}
                    placeholder="What problems are you trying to solve with Aignoz? e.g. 'Scout digital health partnerships', 'Run an internal venture studio', 'Curate our incubator cohort'…" />
                  <div className="text-[10px] text-white/35 mt-1 text-right">{form.useCase.length} / 1000</div>
                </Field>

                <div className="md:col-span-2 flex justify-end mt-2">
                  <Button type="submit" data-testid="inq-submit"
                    className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold px-6">
                    Request a Call <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>

        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 self-start">
          <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80 mb-3">What you get</div>
          <ul className="space-y-3">
            {ENT_SIDEBAR.map((s, i) => {
              const I = s.icon;
              return (
                <li key={i} className="flex items-start gap-2.5 text-xs text-white/80">
                  <I className="w-3.5 h-3.5 text-teal-300/80 mt-0.5 shrink-0" />
                  <span>{s.text}</span>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-white/[0.06] mt-5 pt-4 text-[10px] text-white/40 leading-relaxed">
            All enterprise deployments are bespoke. We'll align scope, security, and rollout in a 30-minute discovery call.
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, error, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[11px] uppercase tracking-[0.14em] text-white/55 block mb-1.5">{label}</label>
      {children}
      {error && <div className="text-[10px] text-rose-300 mt-1">{error}</div>}
    </div>
  );
}
